import React, { useEffect, useState, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  MenuItem,
  Button,
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { useVoice } from "../../context/Voice/VoiceContext";
import * as apiVoice from "../../services/apiVoice";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  group: { marginBottom: 12 },
  label: { fontSize: 12, color: theme.palette.text.secondary, marginBottom: 6 },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 0",
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  rowFirst: { borderTop: "none" },
  val: { flex: 1, fontSize: 13, minWidth: 0 },
  meta: { fontSize: 11, color: theme.palette.text.hint || theme.palette.text.secondary },
  typeChip: {
    fontSize: 10,
    padding: "1px 6px",
    borderRadius: 6,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#f1efe8",
    color: theme.palette.text.secondary,
    marginInlineEnd: 5,
  },
  empty: { fontSize: 12, color: theme.palette.text.secondary, padding: "4px 0" },
  addRow: { display: "flex", gap: 6, marginTop: 8, alignItems: "center", flexWrap: "wrap" },
}));

const TYPES = ["fact", "preference", "risk", "commitment"];

const CustomerMemory = ({ contactId, ticketId }) => {
  const classes = useStyles();
  const { status } = useVoice();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [newType, setNewType] = useState("fact");
  const [newValue, setNewValue] = useState("");

  const load = useCallback(async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const data = await apiVoice.listMemory(contactId);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      /* 403 when disabled */
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    if (status && status.enabled) load();
  }, [status, load]);

  if (!status || !status.enabled) return null;

  const confirmed = items.filter((m) => m.status === "confirmed");
  const suggested = items.filter((m) => m.status === "suggested");

  const run = async (fn) => {
    try {
      await fn();
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const typeLabel = (t) => i18n.t(`voice.memory.types.${t}`) || t;

  const saveEdit = async () => {
    const id = editingId;
    const value = editValue.trim();
    setEditingId(null);
    if (value) await run(() => apiVoice.updateMemory(id, { value }));
  };

  const addMemory = async () => {
    const value = newValue.trim();
    if (!value) return;
    setNewValue("");
    await run(() =>
      apiVoice.createMemory(contactId, { type: newType, value, status: "confirmed" })
    );
  };

  if (loading && !items.length) return <CircularProgress size={18} />;

  return (
    <div>
      <div className={classes.group}>
        <div className={classes.label}>{i18n.t("voice.memory.confirmed")}</div>
        {confirmed.length ? (
          confirmed.map((m, idx) => (
            <div
              key={m.id}
              className={`${classes.row} ${idx === 0 ? classes.rowFirst : ""}`}
            >
              {editingId === m.id ? (
                <>
                  <TextField
                    size="small"
                    variant="outlined"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    style={{ flex: 1 }}
                    autoFocus
                  />
                  <IconButton size="small" onClick={saveEdit}>
                    <CheckIcon fontSize="small" style={{ color: "#27500a" }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => setEditingId(null)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <span className={classes.val}>
                    <span className={classes.typeChip}>{typeLabel(m.type)}</span>
                    {m.value}
                    {m.type === "commitment" && m.dueDate ? ` (${m.dueDate})` : ""}
                  </span>
                  <Tooltip title={i18n.t("voice.memory.edit")}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingId(m.id);
                        setEditValue(m.value);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n.t("voice.memory.delete")}>
                    <IconButton size="small" onClick={() => run(() => apiVoice.deleteMemory(m.id))}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </div>
          ))
        ) : (
          <div className={classes.empty}>{i18n.t("voice.memory.empty")}</div>
        )}

        <div className={classes.addRow}>
          <TextField
            select
            size="small"
            variant="outlined"
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
            style={{ width: 110 }}
          >
            {TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {typeLabel(t)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            variant="outlined"
            placeholder={i18n.t("voice.memory.add")}
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addMemory()}
            style={{ flex: 1, minWidth: 120 }}
          />
          <Button size="small" variant="outlined" onClick={addMemory}>
            {i18n.t("voice.memory.add")}
          </Button>
        </div>
      </div>

      {suggested.length > 0 && (
        <div className={classes.group}>
          <div className={classes.label}>{i18n.t("voice.memory.suggested")}</div>
          {suggested.map((m, idx) => (
            <div key={m.id} className={`${classes.row} ${idx === 0 ? classes.rowFirst : ""}`}>
              <span className={classes.val}>
                {m.value}
                {m.type === "commitment" && m.dueDate ? ` (${m.dueDate})` : ""}
                <div className={classes.meta}>
                  {i18n.t("voice.memory.fromCall")} · {Math.round((m.confidence || 1) * 100)}%
                </div>
              </span>
              <Tooltip title={i18n.t("voice.memory.confirm")}>
                <IconButton size="small" onClick={() => run(() => apiVoice.confirmMemory(m.id, ticketId))}>
                  <CheckIcon fontSize="small" style={{ color: "#27500a" }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={i18n.t("voice.memory.dismiss")}>
                <IconButton size="small" onClick={() => run(() => apiVoice.dismissMemory(m.id))}>
                  <CloseIcon fontSize="small" style={{ color: "#a32d2d" }} />
                </IconButton>
              </Tooltip>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerMemory;
